import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import {
  type ScanDeliveryNotePayload,
  type ScanDeliveryNoteResponse,
} from './types';
import { zodScanDeliveryNoteResponseSchema } from './validations';

/**
 * Uploads one or more delivery note files to DNPv2 ingestor via multipart form-data.
 * Creates one parsing_case row per file and returns their IDs for list/detail/retry queries.
 * OCR and parsing continue asynchronously after this call returns.
 *
 * @param payload - Files and optional metadata to associate with the scan
 * @returns The created parsing case IDs in upload order
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Parsing-Cases/operation/scan_delivery_note_v2_delivery_note_parsing_cases_post
 */
export const scanDeliveryNote = async (
  payload: ScanDeliveryNotePayload,
): Promise<ScanDeliveryNoteResponse> => {
  const {
    dlnFiles,
    accountingReferenceId,
    buyerId,
    forceCreate,
    issuerId,
    note,
    siteId,
  } = payload;

  const formData = new FormData();
  for (const file of dlnFiles) {
    formData.append('dln_file', file);
  }

  if (accountingReferenceId) {
    formData.append('accounting_reference_id', accountingReferenceId);
  }

  if (issuerId) {
    formData.append('issuer_id', issuerId);
  }

  if (buyerId) {
    formData.append('buyer_id', buyerId);
  }

  if (siteId) {
    formData.append('site_id', siteId);
  }

  if (forceCreate !== undefined) {
    formData.append('force_create', String(forceCreate));
  }

  if (note) {
    formData.append('note', note);
  }

  // Ky automatically sets the correct multipart/form-data Content-Type with boundary
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.CREATE_PARSING_CASE(), {
      body: formData,
    })
    .json<{
      parsingCaseId: string;
      parsingCaseIds?: string[];
    }>();

  const normalized: ScanDeliveryNoteResponse = {
    parsingCaseId: response.parsingCaseId,
    parsingCaseIds: response.parsingCaseIds ?? [response.parsingCaseId],
  };

  const validated = zodScanDeliveryNoteResponseSchema.safeParse(normalized);

  if (validated?.error?.issues?.length) {
    log.error({
      message: 'Scan delivery note API response has validation errors',
      error: {
        errors: validated.error.issues,
        errorMessage: validated.error.message,
        response,
      },
    });
  }

  return normalized;
};
